import { useState, useRef, useEffect } from 'react';

interface UseSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
}

/**
 * Swipe Gesture Hook
 * 카드 좌우 스와이프 감지
 */
export const useSwipe = (options: UseSwipeOptions) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    threshold = 100,
  } = options;

  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDistance, setSwipeDistance] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    
    const currentTouch = e.targetTouches[0].clientX;
    const distance = currentTouch - touchStart;
    
    setTouchEnd(currentTouch);
    setSwipeDistance(distance);
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    
    const distance = touchEnd - touchStart;
    
    if (Math.abs(distance) > threshold) {
      if (distance > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (distance < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }
    
    setIsSwiping(false);
    setSwipeDistance(0);
    setTouchStart(0);
    setTouchEnd(0);
  };

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isSwiping,
    swipeDistance,
  };
};

/**
 * 스와이프 가능한 카드 컴포넌트용 스타일
 */
export const getSwipeStyle = (swipeDistance: number) => {
  return {
    transform: `translateX(${swipeDistance}px)`,
    transition: swipeDistance === 0 ? 'transform 0.3s ease-out' : 'none',
  };
};

/**
 * 스와이프 방향 표시기 (좌우)
 */
export const getSwipeIndicator = (swipeDistance: number, threshold: number = 100) => {
  if (Math.abs(swipeDistance) < threshold * 0.3) {
    return null;
  }

  if (swipeDistance > 0) {
    // 오른쪽 스와이프 (승인)
    return {
      direction: 'right',
      opacity: Math.min(Math.abs(swipeDistance) / threshold, 1),
      icon: '✅',
      color: 'text-green-500',
    };
  } else {
    // 왼쪽 스와이프 (거절)
    return {
      direction: 'left',
      opacity: Math.min(Math.abs(swipeDistance) / threshold, 1),
      icon: '❌',
      color: 'text-red-500',
    };
  }
};
